import { Component, input, output, forwardRef, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl } from '@angular/forms';

/**
 * Text input component with label, helper text, and currency variant support.
 * Implements ControlValueAccessor for Angular forms integration.
 *
 * Figma reference:
 * - Standard: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining---Final-Pages?node-id=90-11638
 * - Currency: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining---Final-Pages?node-id=90-11631
 */
@Component({
  selector: 'app-input',
  standalone: false,
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent implements ControlValueAccessor {
  /**
   * The label text displayed above the input
   */
  label = input<string>('');

  /**
   * Helper text displayed in the top-right corner (e.g., "Max. of 50 characters")
   */
  helperText = input<string>('');

  /**
   * Placeholder text shown when input is empty
   */
  placeholder = input<string>('');

  /**
   * Whether the field is required (shows red asterisk)
   */
  required = input<boolean>(false);

  /**
   * Currency code displayed as a suffix (e.g., "USD")
   * When set, the input is treated as a currency input
   */
  currency = input<string>('');

  /**
   * Input type (text, number, email, etc.)
   */
  type = input<string>('text');

  /**
   * Maximum character length
   */
  maxlength = input<number | null>(null);

  /**
   * Whether the input is disabled (for template-driven forms or standalone usage)
   */
  disabled = input<boolean>(false);

  /**
   * Form control for validation state checking
   */
  control = input<AbstractControl | null>(null);

  /**
   * Error message to display when field is invalid
   */
  errorMessage = input<string>('');

  /**
   * Enable currency formatting (thousand separators, 2 decimals)
   */
  enableCurrencyFormatting = input<boolean>(false);

  /**
   * Whether the field has a validation error
   */
  hasError = input<boolean>(false);

  /**
   * Emits when the input value changes
   */
  valueChange = output<string>();

  /**
   * Internal value
   */
  value: string = '';

  /**
   * Internal disabled state (set by reactive forms via setDisabledState)
   */
  private isDisabledByForm = signal<boolean>(false);

  /**
   * Computed disabled state (combines input signal and form control state)
   * Reactive forms disabled state takes precedence
   */
  isDisabled = computed(() => this.isDisabledByForm() || this.disabled());

  /**
   * Check if the control is invalid and touched
   * Using a method instead of computed because form control state changes
   * don't trigger computed signal updates
   */
  isInvalid(): boolean {
    const ctrl = this.control();
    return ctrl ? (ctrl.invalid && ctrl.touched) : false;
  }

  /**
   * Touched state
   * Whether the field has been touched/blurred
   */
  private isTouched = signal<boolean>(false);

  /**
   * Whether to show the error state
   * Shows error only when field has error and is touched
   * Checks both internal touched state and form control touched state
   */
  showError = computed(() => {
    const ctrl = this.control();
    const isControlTouched = ctrl ? ctrl.touched : false;
    return this.hasError() && (this.isTouched() || isControlTouched);
  });

  /**
   * Touched state (legacy property for ControlValueAccessor)
   */
  private touched = false;

  /**
   * onChange callback for ControlValueAccessor
   */
  private onChange: (value: string) => void = () => {};

  /**
   * onTouched callback for ControlValueAccessor
   */
  private onTouched: () => void = () => {};

  /**
   * Handle input changes
   */
  onInput(event: Event): void {
    // Reset touched state when user starts typing
    this.isTouched.set(false);

    // When currency formatting is enabled, the directive handles form control updates
    if (this.enableCurrencyFormatting()) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  /**
   * Handle blur event
   */
  onBlur(): void {
    this.isTouched.set(true);
    this.markAsTouched();
  }

  /**
   * Mark the control as touched
   */
  private markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  // ControlValueAccessor implementation

  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabledByForm.set(isDisabled);
  }
}
